import { http, readRefreshToken } from '@noor/api';
import { env } from '@noor/config';
import {
  loginResponseSchema,
  confirmOtpResponseSchema,
  type LoginCredentials,
  type LoginResponse,
  type SendOtpRequest,
  type ConfirmOtpRequest,
  type ConfirmOtpResponse,
  type SetMerchantPasswordRequest,
  type ChangePasswordRequest,
} from '../model/schema';

/** Unwrap a `{ data: … }` envelope if present (the backend nests payloads). */
function unwrap(raw: unknown): unknown {
  return raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
}

export const sessionApi = {
  /** Merchant login — `POST /api/v1/auth/merchant` → `{ token }`. */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const raw = await http.post<unknown>('api/v1/auth/merchant', credentials, {
      baseURL: env.VITE_CORE_API_URL,
      skipAuthRetry: true,
    });
    return loginResponseSchema.parse(unwrap(raw));
  },

  /**
   * Revoke the session server-side — `POST /api/v1/auth/logout`, authenticated by the
   * access token the http client attaches. Like `auth/merchant` it is absent
   * from `openapi.json` (the spec documents no auth service at all), but the
   * route is live on the gateway: it answers "Missing bearer token" where an
   * unknown path answers "no Route matched".
   *
   * The refresh token rides along in the body so the backend can revoke the
   * whole pair rather than just the short-lived access token; it is omitted
   * when absent instead of sent as `null`. The response is discarded — the
   * caller clears local storage regardless (see `use-sign-out.ts`).
   */
  async logout(): Promise<void> {
    const refreshToken = readRefreshToken();
    await http.post<unknown>(
      'api/v1/auth/logout',
      refreshToken === null ? {} : { refresh_token: refreshToken },
      { baseURL: env.VITE_CORE_API_URL },
    );
  },

  /**
   * Mint and SMS a one-time code — `POST /api/v1/otp/send`.
   *
   * PUBLIC by definition: the caller has forgotten their password, so there is
   * no token to authenticate with. The http client may attach one if a stale
   * session happens to be in storage, which the backend ignores for this route.
   *
   * The response carries no payload (`v1SendOTPResponse` is an empty object) —
   * success is the 2xx itself, so nothing is parsed. A rate-limit or unknown
   * phone surfaces as a thrown `ApiError` for the caller to localize.
   */
  async sendOtp(body: SendOtpRequest): Promise<void> {
    await http.post<unknown>('api/v1/otp/send', body, {
      baseURL: env.VITE_CORE_API_URL,
    });
  },

  /**
   * Confirm a one-time code — `POST /api/v1/otp/confirm`.
   *
   * Exchanges phone + OTP + purpose for a single-use ticket (`confirmation_id`).
   */
  async confirmOtp(body: ConfirmOtpRequest): Promise<ConfirmOtpResponse> {
    const raw = await http.post<unknown>('api/v1/otp/confirm', body, {
      baseURL: env.VITE_CORE_API_URL,
    });
    return confirmOtpResponseSchema.parse(unwrap(raw));
  },

  /**
   * Set a new merchant password — `POST /api/v1/auth/merchants/set-password`.
   *
   * Accepts ticket (`confirmation_id`) and `new_password`.
   */
  async setPassword(body: SetMerchantPasswordRequest): Promise<void> {
    await http.post<unknown>('api/v1/auth/merchants/set-password', body, {
      baseURL: env.VITE_CORE_API_URL,
      skipAuthRetry: true,
    });
  },

  /**
   * Change the password of the SIGNED-IN merchant —
   * `POST /api/v1/auth/change-password`.
   *
   * The only authenticated member of this trio: no phone and no OTP, because
   * the access token the http client attaches already names the account and the
   * old password is what proves the person holding the session is its owner.
   * That is also why a 401 here means "the old password was wrong" rather than
   * "your session expired" — see `use-change-password.ts`, which does NOT let
   * the client's refresh-and-retry reading of a 401 bounce the merchant to
   * `/login` over a typo.
   *
   * Empty-bodied on success (`v1ChangePasswordResponse` is `{}`), so nothing is
   * parsed and no tokens come back — the existing session stays valid.
   */
  async changePassword(body: ChangePasswordRequest): Promise<void> {
    // A 401 from this endpoint means the submitted old password is invalid.
    // Do not run the global refresh/redirect policy, which is for expired
    // sessions and would incorrectly sign the merchant out after a typo.
    await http.post<unknown>('api/v1/auth/change-password', body, {
      baseURL: env.VITE_CORE_API_URL,
      skipAuthRetry: true,
    });
  },
};
